import { ref, watch, onMounted, shallowRef } from 'vue';
import { useChartTheme } from '@rdrive/theme';
import { downloadChartAsPNG } from '@rdrive/helper-chart-export';
import { axisChartOption, withAlpha } from '../../helpers/axisChartOption.js';

// The area gradient's stops, top → bottom, as alphas of the series colour. The old
// SFC wrote the two translucent stops out as literals next to its hard-coded line
// colour; they are derived now, so a palette change can never leave them behind.
const AREA_ALPHA_TOP = 0.5;
const AREA_ALPHA_BOTTOM = 0.1;

export default (props) => {
  // THE colour source — no `chartFontColor` prop. A theme toggle re-evaluates this
  // computed and the watch below repaints, without the page reloading anything.
  const theme = useChartTheme();

  const chartRef = ref(null);
  // shallowRef, not ref: the option goes straight into ECharts, and a deep ref
  // would hand it reactive proxies of every data point. Nothing watches it —
  // `updateChartOption()` assigns it explicitly.
  const chartOption = shallowRef({});

  const isSet = (value) => value !== null && value !== undefined;

  const updateChartOption = () => {
    const rows = props.rows ?? [];
    const palette = theme.value.series;
    // Opt-in (asset-management): the caller's theme colour over the palette slot
    const color = isSet(props.color)
      ? props.color
      : palette[((props.colorIndex % palette.length) + palette.length) % palette.length];

    const base = axisChartOption({
      theme: theme.value,
      orientation: 'vertical',
      categories: rows.map((row) => row.label),
      // Opt-in (asset-management): the value axis' own formatter; the tooltip keeps formatValue
      formatValue: props.formatAxisValue ?? props.formatValue,
      labelRotate: props.labelRotate,
      // Opt-in (asset-management): axisChartOption sets min / max only when given
      valueMin: props.valueMin,
      valueMax: props.valueMax,
    });

    const series = {
      name: props.seriesName,
      type: 'line',
      data: rows.map((row) => row.value),
      smooth: props.smooth,
      itemStyle: { color },
    };
    // Opt-ins (asset-management): a key only when asked for — a null row bridged, bigger markers
    if (props.connectNulls) series.connectNulls = true;
    if (isSet(props.symbolSize)) series.symbolSize = props.symbolSize;
    if (props.area) {
      series.areaStyle = {
        color: {
          type: 'linear',
          x: 0,
          y: 0,
          x2: 0,
          y2: 1,
          colorStops: [
            { offset: 0, color: withAlpha(color, AREA_ALPHA_TOP) },
            { offset: 1, color: withAlpha(color, AREA_ALPHA_BOTTOM) },
          ],
        },
      };
    }

    chartOption.value = {
      ...base,
      xAxis: {
        ...base.xAxis,
        // The line starts at the axis, as it did before
        boundaryGap: false,
        // ...and the labels thin themselves out again. The shared helper pins
        // `interval: 0` for a bar chart's handful of categories; a time series has
        // one category per day, and forcing every one of them to render turns the
        // axis into a smear. Back to ECharts' default.
        axisLabel: { ...base.xAxis.axisLabel, interval: 'auto' },
      },
      tooltip: {
        ...base.tooltip,
        formatter: (params) => {
          const point = Array.isArray(params) ? params[0] : params;
          if (!point) return '';
          const label = point.axisValue ?? rows[point.dataIndex]?.label ?? '';
          return `<strong>${label}</strong><br/>${props.seriesName}: ${props.formatValue(point.value)}`;
        },
      },
      series: [series],
    };
  };

  // Rows BY REFERENCE (no deep watch): the app adapter recomputes the whole array.
  // `formatValue` is deliberately not watched — it only ever changes together with
  // the rows it formats (a metric toggle swaps both); nor is the opt-in `formatAxisValue`,
  // for the same reason. The other opt-ins are (a theme toggle hands over a new `color`).
  watch(
    [
      () => props.rows,
      () => props.seriesName,
      () => props.area,
      () => props.smooth,
      () => props.colorIndex,
      () => props.labelRotate,
      () => props.color,
      () => props.connectNulls,
      () => props.symbolSize,
      () => props.valueMin,
      () => props.valueMax,
      theme,
    ],
    updateChartOption,
  );

  onMounted(updateChartOption);

  // Opt-in PNG export, called by the page (template ref) — no legend: one line, named by the card
  const downloadPng = () => {
    const chart = chartRef.value?.chart;
    if (!chart || !props.exportFilename) return;
    downloadChartAsPNG(chart, props.exportFilename, null, {
      mode: theme.value.mode,
      legend: false,
      title: props.exportTitle,
    });
  };

  return {
    // `chartRef` is a STRING template ref (`ref="chartRef"` in component.vue) — it
    // only binds because it is returned under that exact name.
    chartRef,
    chartOption,
    downloadPng,
  };
};
